import { useMemo } from 'react';
import { rawColorPalette } from '@/modules/shared/DataLayers/utils/sensorDataColorMap';

function getEnabledGraphsMap(graphs: any[]) {
  return graphs.reduce((result, graph) => {
    result[graph.id] = graph.enabled;
    return result;
  }, {} as Record<string, boolean>);
}

function extractSensorEnableMap(dataLayers: any[]): Record<string, Record<string, boolean>> {
  const result: Record<string, Record<string, boolean>> = {};
  dataLayers?.forEach((graph) => {
    const id = graph.id;
    const legendItems = graph?.legend ?? [];
    if (!id) return;
    if (!result[id]) {
      result[id] = {};
    }
    legendItems.forEach((entry: any) => {
      const label = entry.displayName;
      if (typeof label === 'string') {
        // Use the same logic for both sensors and alarms - respect backend enabled state
        const isEnabled = entry.enabled !== false;
        result[id][label] = isEnabled;
      }
    });
  });
  return result;
}

function getInitialFeatures(graphs: any[]): Record<string, Record<string, boolean>> {
  return Object.fromEntries(
    graphs
      .filter((el: any) => el.features?.length > 0)
      .map((el: any) => [
        el.id,
        Object.fromEntries(el.features.map((feature: any) => [feature.id, !!feature.isDefault])),
      ])
  );
}

export function useSectionConfig(sectionConfigGraphs: any[], enabledGraphs?: Record<string, boolean>) {
  const graphs = useMemo(() => {
    if (!sectionConfigGraphs || sectionConfigGraphs.length === 0) return [];
    return sectionConfigGraphs.map((graph: any) => ({
      ...graph,
      enabled: enabledGraphs?.[graph.id] ?? graph.enabled ?? true,
    }));
  }, [JSON.stringify(sectionConfigGraphs), enabledGraphs]);

  const configMap = useMemo(() => {
    return graphs.reduce((acc, graph) => {
      acc[graph.id] = {
        ...graph,
        hasFeatures: graph.features?.length > 0,
        sensorUiNames: graph.legend?.map((l: any) => l.displayName) || [],
        colors: graph.colors || rawColorPalette, // Use graph's colors if available, otherwise fallback to palette
      };
      return acc;
    }, {} as Record<string, any>);
  }, [graphs]);

  const availableSections = useMemo(() => graphs.map((el: any) => el.id), [graphs]);

  const enabledSections = useMemo(() => getEnabledGraphsMap(graphs), [graphs]);

  const splitDefault = useMemo(
    () => Object.fromEntries(graphs.filter((el: any) => el.splitDefault).map((el: any) => [el.id, true])),
    [graphs]
  );

  const sensorEnableMap = useMemo(() => extractSensorEnableMap(graphs), [graphs]);

  const initialFeatures = useMemo(() => getInitialFeatures(graphs), [graphs]);

  return {
    normalizedGraphs: graphs,
    sectionConfig: configMap,
    availableSections,
    enabledSections,
    splitDefault,
    sensorEnableMap,
    initialFeatures,
  };
}
